import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router";
import {
    TextField, Button, Typography, Box, Paper, MenuItem,
    FormControl, Select, InputLabel, Divider
} from '@mui/material';
import { useUser } from '../components/UserContext';
import { useApiClient } from '../components/useApiClient';
import { Client } from "../models/Client";
import useAuthToken from '../functions/useAuthToken';
import { UserRecord, ClientAccess } from "../models/UserProfile";

//Error Handling
import { useErrorHandler, isNullOrEmpty } from '../hooks/useErrorHandler';
import ErrorSummary from '../components/ErrorHandler/ErrorSummary';

// Category template
import ClientCategoryTemplateSelector from "../components/Categories/ClientCategoryTemplateSelector";
import { CategoryTemplate, TemplateCategory, TemplateCategoryPreviewGroup } from '../models/Category';

//License Check
import type { LicenseCheck } from "../models/License";
import { useLicenseChecker } from '../hooks/useLicenseCheck';
import LicenseExceededDialog from "../components/LicenseManagement/LicenseExceededDialog";

const ClientSettingsPage: React.FC = () => {
    const { user, activeClientId, invalidateUser, getClientInfo } = useUser();
    const { get, put, post } = useApiClient();
    const getAccessToken = useAuthToken();

    const { errors, handleError, clearErrors, validateRequired, hasErrors } = useErrorHandler();

    const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);

    const [selectedClientId, setSelectedClientId] = useState<number | null>(activeClientId || null);
    const selectedClient = getClientInfo(user?.clients, selectedClientId);
    const canEditClient = selectedClient ? (selectedClient.roleCode == "Admin" || selectedClient.roleCode == "Owner" || selectedClient.roleCode == "Valuer") : false;

    const [clientName, setClientName] = useState('');
    const [newClientName, setNewClientName] = useState('');
    const [status, setStatus] = useState('');

    const navigate = useNavigate();

    //License Checker
    const { canAdd } = useLicenseChecker();
    const [licenseCheck, setLicenseCheck] = useState<LicenseCheck | null>(null);
    const isTenantAdmin = true;
    const [licenseDialogOpen, setLicenseDialogOpen] = useState(false);

    const handleUpgrade = () => {
        setLicenseDialogOpen(false);
        navigate("/admin/tenant");
    };

    useEffect(() => {
        const loadClient = async () => {
            if (!selectedClientId) return;
            try {
                const accessToken = await getAccessToken();
                if (!accessToken) return;

                const res = await get<Client>(`clients/${selectedClientId}`, accessToken);
                setClientName(res.clientName);

            } catch (err) {
                console.error('Error loading client name', err);
            }
        };
        loadClient();
    }, [selectedClientId]);

    const handleTemplateContinue = (templateId: number | null) => {
        setSelectedTemplateId(templateId);
    };

    const handleUpdate = async () => {

        //Validate Form
        clearErrors();
        let isValid = true;

        if (!validateRequired(clientName, 'Client', 'A Client name is required.')) isValid = false;

        if (isValid) {

            const accessToken = await getAccessToken();

            if (!accessToken || !selectedClientId) return;

            try {
                const formData = new FormData();
                formData.append("ClientId", selectedClientId.toString());
                formData.append("ClientName", clientName);

                await put(`clients/${selectedClientId}`, accessToken, formData);
                //await refreshUser();
                invalidateUser();

                setStatus('Client name updated');

            } catch (err) {
                setStatus('Failed to update');
                console.error(err);
            }
        }
    };

    const handleCreate = async () => {

        const result = await canAdd("client");

        if (!result.allowed) {
            setLicenseCheck(result);
            setLicenseDialogOpen(true);
            return;
        }

        //Validate Form
        clearErrors();
        let isValid = true;

        if (!validateRequired(newClientName, 'Client', 'A Client name is required.')) isValid = false;

        if (selectedTemplateId == null) {
            handleError({ code: "Client", message: "Please select a category template to initialise the client/collection.", details: "" });
            isValid = false;
        }

        if (isValid) {

            const accessToken = await getAccessToken();
            if (!accessToken) return;

            try {
                const formData = new FormData();
                formData.append("ClientId", "0");
                formData.append("ClientName", newClientName);
                formData.append("CategoryTemplateId", selectedTemplateId?.toString() || '');

                const newClient = await post<Client>('clients/create', accessToken, formData);

                //await refreshUser();
                invalidateUser();

                setSelectedClientId(newClient.clientId);
                setStatus('New client created');
                setClientName(newClient.clientName);
                setNewClientName("");
            }
            catch (e: any) {
                const data = e?.response?.data;
                if (data?.code === "LICENSE_LIMIT_EXCEEDED") {
                    setStatus(`${data.message} (${data.current}/${data.max})`);
                    return;
                }
                setStatus('Failed to create client');
            }
        }
    };

    return (
        <Box sx={{ maxWidth: 500, mx: 'auto', mt: 4 }}>
            <Paper sx={{ p: 3 }}>
                {/* Error Panel*/}
                <Box>
                    <ErrorSummary errors={errors} title="Client Detail Errors" />
                </Box>

                <Typography variant="h6" gutterBottom>
                    Edit Existing Client
                </Typography>

                <FormControl fullWidth margin="normal" size="small">
                    <InputLabel id="client-select-label">Select Client</InputLabel>
                    <Select
                        labelId="client-select-label"
                        value={selectedClientId || ''}
                        onChange={(e) => setSelectedClientId(Number(e.target.value))}
                        label="Select Client"
                    >
                        {user?.clients?.map((c) => (
                            <MenuItem key={c.clientId} value={c.clientId}>
                                {c.clientName} ({c.roleName})
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    fullWidth
                    label="Client Name"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    margin="normal"
                    size="small"
                    disabled={!canEditClient}
                />
                
                <Button variant="contained" onClick={handleUpdate} disabled={!canEditClient}>
                    Save Changes
                </Button>

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                    Create New Client
                </Typography>
                <TextField
                    fullWidth
                    label="New Client Name"
                    value={newClientName}
                    onChange={(e) => setNewClientName(e.target.value)}
                    margin="normal"
                    size="small"
                />

                <ClientCategoryTemplateSelector onContinue={handleTemplateContinue} />

                <Divider sx={{ my: 2 }} />

                <Button variant="outlined" onClick={handleCreate}>
                    Create
                </Button>

                {status && (
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        {status}
                    </Typography>
                )}
            </Paper>

            <LicenseExceededDialog
                open={licenseDialogOpen}
                onClose={() => setLicenseDialogOpen(false)}
                licenseCheck={licenseCheck}
                onUpgrade={handleUpgrade}
                isTenantAdmin={isTenantAdmin}
            />
        </Box>
    );
};

export default ClientSettingsPage;
